import * as React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Segment, Grid, Form, Header, Button } from 'semantic-grab-ui/src';

import formComponent from 'components/decorators/form_component';
import FormInputWithError from 'components/form/FormInputWithError';
import { Campaign } from 'types/ts/campaigns';
import { targetUserTypes } from 'data/trident-types';

import { getSegmentName, getSegmentNamespace } from './constants/utils';
import { getEnumerationParquetFilepath } from './parquet-user-attributes-preview-utils';
import SegPWidget from './SegPWidget';
import SegPInsightsWidget from './SegPInsightsWidget';
import { doSegmentValidation, SegmentParams } from 'utils/segp-utils';

interface Props {
  isAdd: boolean,
  parentNode: string,
  currentNode: string,
  onModelChange: Function,
  handleCloseModal: Function,
  handleDeleteNode: Function,
  handleInputChange: Function,
  handleControlBlur: Function,
  addDefaultExperiment: Function,
  model: Campaign,
  countryPercentageWarningThreshold: number,
  countries: any,
  featureFlagConfig: any,
}

interface State {
  lastSelectedSegment: string;
  countryBreakDownAvailable: boolean;
  differentCountryPercent: number;
  originalModel: any,
  segmentSize: number,
}

@formComponent()
class EnumerationForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      originalModel: this.props.model,
      lastSelectedSegment: _.get(props.model, `nodes.${props.currentNode}.data.payload.names[0]`, ''),
      countryBreakDownAvailable: false,
      differentCountryPercent: 0, // percentage of users in the segment that are not in campaign's country
      segmentSize: 0,
    };
  }

  onSegmentDataLoaded = (data) => {
    const segmentSize = _.get(data, 'segmentSize', 0);
    if (_.isEmpty(data) || segmentSize === 0) {
      this.setState({
        differentCountryPercent: 0,
        segmentSize: 0,
      });
      return;
    }

    // get campaign country
    const campaignCountryID = _.get(this.props, 'model.countryID');
    const countryName = _.get(
      _.find(this.props.countries, c => c.countryID === campaignCountryID),
      'name',
    );

    // count how many users in the segment are NOT in the campaign country
    const countryBreakdown = _.get(data, 'pax:country.attributes', []);
    const diffCountries = _.filter(countryBreakdown, x => x.name !== countryName);
    const diffCountryUserCount = _.sum(
      _.map(diffCountries, c => _.get(c, 'value', 0))
    );

    this.setState({
      differentCountryPercent: _.round(diffCountryUserCount / segmentSize * 100.0, 2),
      countryBreakDownAvailable: !_.isEmpty(countryBreakdown),
      segmentSize,
    });
  };

  save = () => {
    const { model, currentNode, parentNode, countryPercentageWarningThreshold, featureFlagConfig } = this.props;
    const { differentCountryPercent, countryBreakDownAvailable, segmentSize } = this.state;
    const payload = _.get(model, `nodes.${currentNode}.data.payload`, {});
    const isParquet = !_.isEmpty(getEnumerationParquetFilepath(payload));

    if (!isParquet) {
      const params: SegmentParams = {
        segmentName: _.get(model, `nodes.${currentNode}.data.payload.names[0]`, ''),
        countryPercentageWarningThreshold,
        featureFlagConfig,
        targetUserType: model.targetUserType,
        countryID: model.countryID,
        campaignID: model.campaignID,
        differentCountryPercent,
        countryBreakDownAvailable,
        segmentSize,
      };
      const errorMessage = doSegmentValidation(params);
      if (!_.isEmpty(errorMessage)) {
        alert(errorMessage);
        return;
      }

      // add segment size
      if (segmentSize) {
        _.set(model, `nodes.${currentNode}.data.payload.size`, segmentSize);
      }
    }

    // auto-holdback feature
    const autoholdbackEnabled = _.get(model, `nodes.${parentNode}.autoHoldbackEnabled`, false);
    const childNodes = _.get(model, `nodes.${currentNode}.children`, []);
    if (autoholdbackEnabled && _.isEmpty(childNodes) && (model.targetUserType === targetUserTypes.pax || model.targetUserType === targetUserTypes.dax)) {
      this.props.addDefaultExperiment(currentNode);
    }

    this.props.handleCloseModal();
  };

  cancel = () => {
    // rollback the change
    if (this.props.isAdd) {
      if (!confirm('Cancel will delete this inactive node. Are you sure to delete this?')) {
        return;
      }
      this.props.handleDeleteNode(this.props.parentNode, this.props.currentNode, -1);
    } else {
      this.props.onModelChange(this.state.originalModel);
    }
    this.props.handleCloseModal();
  };

  render() {
    const { model, currentNode } = this.props;
    const { targetUserType } = model;
    const isDaxUserType = targetUserType === targetUserTypes.dax || targetUserType === targetUserTypes.moveITDax;
    const lastSelectedSegment = _.get(this.state, 'lastSelectedSegment');
    const segment = _.get(model, `nodes.${currentNode}.data.payload.names[0]`, '');
    const segmentName = getSegmentName(segment);
    const segmentNamespace = getSegmentNamespace(segment);
    const bottomPlaceHolderStyle = {
      minHeight: 200,
    };
    const isSegpWidgetEnabled = _.get(window.Grab.config, 'SEGP_WIDGET_ENABLED', true);
    const payload = _.get(model, `nodes.${currentNode}.data.payload`, {});
    const parquetFilepath = getEnumerationParquetFilepath(payload);
    const isParquet = !_.isEmpty(parquetFilepath);

    let segmentSectionContent;
    if (isParquet) {
      segmentSectionContent = (
        <Grid.Column width={16}>
          <div style={{ wordBreak: 'break-all', padding: '8px 0' }}>
            {parquetFilepath}
          </div>
        </Grid.Column>
      );
    } else if (isSegpWidgetEnabled) {
      segmentSectionContent = (<SegPWidget
        targetUserType={targetUserType}
        onChange={(val) => this.props.handleInputChange({}, {
          name: `nodes.${currentNode}.data.payload.names[0]`,
          value: val,
        })}
        onSelect={(val) => {
          this.setState({
            lastSelectedSegment: val,
          });
        }}
        segmentVal={segment}
        maxVisible={30}
        enabledSegmentNSFormat
        showNsDropdown
      />);
    } else {
      segmentSectionContent = (<Form.Input
        label="Segment Name"
        name={`nodes.${currentNode}.data.payload.names[0]`}
        value={segment}
        onBlur={this.props.handleControlBlur}
        onChange={this.props.handleInputChange}
      />);
    }

    return (
      <div>
        <div className="model-button">
          <Grid>
            <Grid.Column textAlign="right">
              <Button.Group>
                <Button
                  onClick={this.cancel}
                >
                  Cancel
                </Button>
                <Button.Or />
                <Button
                  positive
                  onClick={this.save}
                >
                  Save
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid>
        </div>

        <div className="treatment-form">
          <Segment className="treatment-form-segment">
            <Grid>
              <Grid.Column width={12}>
                <Header as="h3">Enumeration Name</Header>
              </Grid.Column>
            </Grid>
            <Grid>
              <Grid.Column width={12}>
                <Form>
                  <Grid>
                    <Grid.Column width={12}>
                      <FormInputWithError
                        name={'nodes.'.concat(`${currentNode}`, '.name')}
                        {...this.props}
                      >
                        <Form.Input
                          required
                          value={model.nodes[currentNode].name}
                          onChange={this.props.handleInputChange}
                          onBlur={this.props.handleControlBlur}
                        />
                      </FormInputWithError>
                    </Grid.Column>
                  </Grid>
                </Form>
              </Grid.Column>
            </Grid>
          </Segment>

          <Segment className="treatment-form-segment">
            <Grid>
              <Grid.Column width={12}>
                <Header as="h3">
                  {isParquet ? 'File path' : `Segment ${isDaxUserType ? 'ID' : 'Name'}`}
                </Header>
              </Grid.Column>
            </Grid>
            <Grid>
              {segmentSectionContent}
            </Grid>
          </Segment>

          {
            !isParquet &&
            (targetUserType === targetUserTypes.pax || targetUserType === targetUserTypes.moveITPax) && !_.isEmpty(segmentName) && (lastSelectedSegment === segment) &&
            <SegPInsightsWidget
              segmentID={segmentName}
              namespace={segmentNamespace}
              onDataLoaded={this.onSegmentDataLoaded}
            />
          }
          <p style={bottomPlaceHolderStyle} />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    countryPercentageWarningThreshold: _.get(state, 'newTridents.segPConfig.countryPercentageWarningThreshold', 5),
    featureFlagConfig: _.get(state, 'newTridents.featureFlagConfig', {}),
    countries: state.countries.entities,
  };
}

export default connect(mapStateToProps, {})(EnumerationForm);
